import { Typography, FormControlLabel, Checkbox } from "@mui/material";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
/* eslint-disable react/no-unknown-property -- project-standard Cypress selectors */

import InputOICH from "../resuable/input_oich";
import PropTypes from "prop-types";
import { useWatchInput } from "../../hooks/useWatchInput";
import { formatTime } from "../utils";
import { formatDate } from "../calendar_component/calendar_component";
import AppointmentFormStatus from "./appointment_form_status";

const AppointmentRequestForm = ({
  appointment_state_request_state,
  to_do_list_state,
  initialState,
  showTitle,
  user_data
}) => {
  const [date, setDate] = useState(formatDate(new Date()));
  const [hasEditedDate, setHasEditedDate] = useState(false);
  const [location, setLocation] = useState("");
  const [pickupLocation, setPickupLocation] = useState("");
  const [hasEditedPickupLocation, setHasEditedPickupLocation] = useState(false);
  const [description, setDescription] = useState("");
  const [hour, setHour] = useState();
  const [minute, setMinute] = useState();
  const [transportation, setTransportation] = useState("");
  const [uberApprover, setUberApprover] = useState("");
  const [safetyConsiderations, setSafetyConsiderations] = useState("");
  const [wholeDayReservation, setWholeDayReservation] = useState(false);

  useEffect(() => {
    updateInitialState();
  }, [to_do_list_state, appointment_state_request_state]);

  const watchedEditedDateValue = useWatchInput({ id: "to_do_list_date" });
  useEffect(() => {
    if (!hasEditedDate && watchedEditedDateValue) {
      setDate(watchedEditedDateValue);
    }
  }, [watchedEditedDateValue]);

  const watchedMinutes = useWatchInput({ id: "to_do_list_time_user_selected_minute" });
  useEffect(() => {
    if (watchedMinutes) setMinute(watchedMinutes);
  }, [watchedMinutes]);

  const watchedHour = useWatchInput({ id: "to_do_list_time_user_selected_hour" });
  useEffect(() => {
    if (watchedHour) setHour(watchedHour);
  }, [watchedHour]);

  const watchedClient = useWatchInput({ id: "client_autocomplete_selected_client" });
  useEffect(() => {
    if (hasEditedPickupLocation || watchedClient === undefined) return;
    if (!watchedClient) {
      setPickupLocation("");
      return;
    }

    try {
      const client = JSON.parse(watchedClient);
      setPickupLocation(client?.location || "");
    } catch (_error) {
      setPickupLocation("");
    }
  }, [hasEditedPickupLocation, watchedClient]);

  const updateInitialState = () => {
    const setTimeFromValue = (timeValue) => {
      if (!timeValue) return;

      if (typeof timeValue === "string" && /^\d{1,2}:\d{2}$/.test(timeValue)) {
        const [hours, minutes] = timeValue.split(":");
        setHour(hours.padStart(2, "0"));
        setMinute(minutes);
        return;
      }

      const formattedTime = new Date(timeValue);
      if (formattedTime.toString() === "Invalid Date") return;

      const serializedLocalTime =
        typeof timeValue === "string" ? timeValue.match(/T(\d{2}):(\d{2})/) : null;
      const hours = serializedLocalTime?.[1] || formattedTime.getHours();
      const minutes = serializedLocalTime?.[2] || formattedTime.getMinutes();
      setHour(String(hours).padStart(2, "0"));
      setMinute(String(minutes).padStart(2, "0"));
    };

    // Initial payload (e.g. edit page server-rendered state)
    if (initialState) {
      setDate(initialState.date || to_do_list_state?.to_do_date || formatDate(new Date()));
      setLocation(initialState.location || to_do_list_state?.location || "");
      setPickupLocation(initialState.pickup_location || initialState.pickupLocation || "");
      setTimeFromValue(initialState.time || to_do_list_state?.to_do_list_time);
      return;
    }

    // Existing appointment (edit)
    if (appointment_state_request_state) {
      if (appointment_state_request_state.date) {
        setDate(appointment_state_request_state.date);
        if (to_do_list_state && to_do_list_state.to_do_date != appointment_state_request_state.date)
          setHasEditedDate(true);
      } else if (to_do_list_state?.to_do_date) {
        setDate(to_do_list_state.to_do_date);
      }

      if (appointment_state_request_state.location)
        setLocation(appointment_state_request_state.location);
      setPickupLocation(appointment_state_request_state.pickup_location || "");
      if (
        appointment_state_request_state.description &&
        appointment_state_request_state.description !=
          appointment_state_request_state.task_description
      )
        setDescription(appointment_state_request_state.description);
      if (appointment_state_request_state.suggested_transportation) {
        setTransportation(appointment_state_request_state.suggested_transportation);
      }
      if (appointment_state_request_state.uber_approver) {
        setUberApprover(appointment_state_request_state.uber_approver);
      }
      if (appointment_state_request_state.safety_considerations) {
        setSafetyConsiderations(appointment_state_request_state.safety_considerations);
      }
      if (appointment_state_request_state.whole_day_reservation) {
        setWholeDayReservation(appointment_state_request_state.whole_day_reservation);
      }
      setTimeFromValue(
        appointment_state_request_state.time_string || appointment_state_request_state.time
      );
      return;
    }

    // New appointment defaults pulled from the to-do list
    if (to_do_list_state?.to_do_date) {
      setDate(to_do_list_state.to_do_date);
    }
    if (to_do_list_state?.to_do_list_time) {
      setTimeFromValue(to_do_list_state.to_do_list_time);
    }
  };

  return (
    <div>
      <input type="hidden" name="has_appointment_request" value={true} />
      <input type="hidden" name="appointment_request_location" value={location} />
      <input type="hidden" name="appointment_request_pickup_location" value={pickupLocation} />
      <input
        type="hidden"
        name="appointment_request_date"
        value={date}
        explorator_test_id="appointment-request-date"
      />
      <input type="hidden" name="appointment_request_description" value={description} />
      <input
        type="hidden"
        name="appointment_request_time"
        value={wholeDayReservation ? "" : formatTime(hour, minute)}
      />
      <input type="hidden" name="appointment_request_transportation" value={transportation || ""} />
      <input type="hidden" name="appointment_request_uber_approver" value={uberApprover || ""} />
      <input
        type="hidden"
        name="appointment_request_safety_considerations"
        value={safetyConsiderations || ""}
      />
      <input
        type="hidden"
        name="appointment_request_whole_day_reservation"
        value={wholeDayReservation ? "true" : "false"}
      />
      {appointment_state_request_state && appointment_state_request_state.status && (
        <AppointmentFormStatus
          user_data={user_data}
          appointment_state_request_state={appointment_state_request_state}
        />
      )}
      <DateDisplay>Date: {date}</DateDisplay>
      {showTitle && <Typography variant="h6">Appointment Request Form:</Typography>}
      <StyledFormGroup>
        <WholeDayContainer>
          <FormControlLabel
            control={
              <Checkbox
                checked={wholeDayReservation}
                onChange={(e) => {
                  setWholeDayReservation(e.target.checked);
                  if (e.target.checked) {
                    setHour("");
                    setMinute("");
                  }
                }}
              />
            }
            label="Whole Day Reservation (Surgery, etc.)"
          />
          <WholeDayHelperText>
            For appointments where the exact time is unknown. Blocks the peer&apos;s entire day.
          </WholeDayHelperText>
        </WholeDayContainer>
        {!wholeDayReservation && (
          <TimeInputAndLabelContainer>
            <label className="label">Time Input In 24 Hour Format. (E.X 16:20)</label>
            <TimeInputsContainer>
              <TimeInputs
                className="input valid"
                value={hour}
                onChange={(event) => {
                  const newHour = event.target.value;
                  setHour(newHour);
                  window.dispatchEvent(
                    new CustomEvent("appointmentTimeChanged", {
                      detail: { hour: newHour, minute }
                    })
                  );
                }}
              />
              <TimeInputSeparator>:</TimeInputSeparator>
              <TimeInputs
                className="input valid"
                value={minute}
                onChange={(event) => {
                  const newMinute = event.target.value;
                  setMinute(newMinute);
                  window.dispatchEvent(
                    new CustomEvent("appointmentTimeChanged", {
                      detail: { hour, minute: newMinute }
                    })
                  );
                }}
              />
            </TimeInputsContainer>
          </TimeInputAndLabelContainer>
        )}
        <InputOICH
          id="appointment_request_destination"
          exploratorTestId="appointment-request-destination"
          label="Destination"
          value={location}
          fullWidth
          onChange={(event) => {
            setLocation(event.target.value);
          }}
        />
        <InputExtraInformation>
          Where the client is going. If left blank, the attached task location is used.
        </InputExtraInformation>
        <InputOICH
          id="appointment_request_pickup_location"
          exploratorTestId="appointment-request-pickup-location"
          label="Pickup location"
          required
          value={pickupLocation}
          fullWidth
          onChange={(event) => {
            setPickupLocation(event.target.value);
            setHasEditedPickupLocation(true);
          }}
        />
        <InputExtraInformation>
          Required. Prefilled from the linked client and editable for this appointment.
        </InputExtraInformation>
        <InputOICH
          label="Description"
          value={description}
          onChange={(event) => {
            setDescription(event.target.value);
          }}
        />
        <InputExtraInformation>
          *If description left blank, it will use the attached to do list description.
        </InputExtraInformation>
        <TransportRow>
          <label className="label">Suggested mode of transportation</label>
          <select
            explorator_test_id="appointment-request-transportation"
            className="input valid"
            value={transportation}
            onChange={(event) => {
              setTransportation(event.target.value);
              if (event.target.value !== "Uber") setUberApprover("");
            }}
          >
            <option value="">Select an option</option>
            <option value="Bus/Train">Bus/Train</option>
            <option value="Para transpo">Para transpo</option>
            <option value="Taxi">Taxi</option>
            <option value="Uber">Uber</option>
          </select>
        </TransportRow>
        {transportation === "Uber" && (
          <InputOICH
            exploratorTestId="appointment-request-uber-approver"
            label="Nurse/manager who approved (required for Uber)"
            value={uberApprover}
            onChange={(event) => setUberApprover(event.target.value)}
          />
        )}
        <SafetyTextAreaContainer>
          <label className="label">
            Safety considerations (behaviors, mobility, allergies, food/drink, pour carry)
          </label>
          <textarea
            explorator_test_id="appointment-request-safety-considerations"
            className="textarea"
            value={safetyConsiderations}
            onChange={(event) => setSafetyConsiderations(event.target.value)}
            rows={3}
          />
        </SafetyTextAreaContainer>
        {/* Disabled as it makes workflow easier, but keeping in incase we need a different date in the future. <InputOICH
          type="date"
          label="Date"
          value={date}
          onChange={(event) => {
            setDate(event.target.value);
            setHasEditedDate(true);
          }}
        />
        <InputExtraInformation>
          *If date left blank, it will use the attached to do list description.
        </InputExtraInformation> */}
      </StyledFormGroup>
    </div>
  );
};

AppointmentRequestForm.propTypes = {
  appointment_state_request_state: PropTypes.object,
  to_do_list_state: PropTypes.object,
  initialState: PropTypes.object,
  showTitle: PropTypes.bool,
  user_data: PropTypes.object
};

export default AppointmentRequestForm;

const StyledFormGroup = styled.div``;

const TimeInputAndLabelContainer = styled.div`
  width: fit-content;
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const TimeInputsContainer = styled.div`
  display: flex;
`;

const TimeInputs = styled.input`
  max-width: 5rem;
`;

const TimeInputSeparator = styled.p`
  width: fit-content;
  height: fit-content;
  margin: auto 0.75rem;
`;

const TransportRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-top: 1rem;
  max-width: 16rem;
`;

const InputExtraInformation = styled.p`
  font-size: 1rem;
  color: darkgrey;
`;

const DateDisplay = styled.p`
  font-size: 2rem;
`;

const SafetyTextAreaContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  max-width: 24rem;
  gap: 0.35rem;
`;

const WholeDayContainer = styled.div`
  margin-top: 1rem;
  margin-bottom: 0.5rem;
`;

const WholeDayHelperText = styled.p`
  font-size: 1rem;
  color: #666;
  margin: 0.25rem 0 0 2rem;
`;
